/**
 * Live inputs for the admin menu's action numbers (see lib/adminActions).
 *
 * Only open work is read: orders still pending / staffed / running (plus a
 * finished trade only while a worker report on it waits for RM24), and the
 * timesheets that can need RM24 (a question, a client rejection, a day still
 * open, hours waiting on the client). History is never streamed for a badge.
 */
import type { Job } from '@rendimees24/shared-types';
import { collection, documentId, onSnapshot, query, where, type Query } from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';

import { leaveAction, ordersAction, timesheetsAction, type ActionCount } from '../lib/adminActions';

import { getFirebase } from './firebase';
import { useAllPendingReports } from './orderReports';
import { useTimesheetsByStatus } from './timesheets';
import { usePendingLeaveRequests } from './useLeaveRequests';
import { useNow } from './useNow';

const OPEN_JOB_STATUSES = ['pending', 'assigned', 'in_progress'];

/** Merge several live queries into one list, de-duplicated by document path. */
function useMergedSnapshots<T>(make: () => Query[], key = ''): T[] {
  const [rows, setRows] = useState<T[]>([]);
  useEffect(() => {
    const parts = new Map<number, Map<string, T>>();
    const publish = () => {
      const all = new Map<string, T>();
      for (const part of parts.values()) for (const [k, v] of part) all.set(k, v);
      setRows([...all.values()]);
    };
    const unsubs = make().map((q, i) =>
      onSnapshot(
        q,
        (snap) => {
          parts.set(
            i,
            new Map(
              snap.docs.map((d) => [
                d.ref.path,
                { ...d.data(), id: d.id, jobId: d.ref.parent.parent?.id ?? '' } as T,
              ]),
            ),
          );
          publish();
        },
        // A failed read shows no number rather than a wrong one.
        () => undefined,
      ),
    );
    if (unsubs.length === 0) setRows([]);
    return () => unsubs.forEach((u) => u());
    // `key` names the queries; `make` is rebuilt every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return rows;
}

export interface AdminActionCounts {
  jobs: ActionCount;
  timesheets: ActionCount;
  leaveRequests: ActionCount;
}

export function useAdminActionCounts(): AdminActionCounts {
  const now = useNow();
  const openJobs = useMergedSnapshots<Job>(() => {
    const { firestore } = getFirebase();
    return [query(collection(firestore, 'jobs'), where('status', 'in', OPEN_JOB_STATUSES))];
  }, 'open');
  // Worker reports waiting for RM24 — on a finished trade too, which the open
  // query does not bring: those trades are read by id (30 per query).
  const reports = useAllPendingReports();
  const reportCounts = reports.known ? reports.counts : undefined;
  const openIds = new Set(openJobs.map((j) => j.id));
  const extraIds = Object.keys(reportCounts ?? {})
    .filter((id) => !openIds.has(id))
    .sort();
  const finishedWithReports = useMergedSnapshots<Job>(() => {
    const { firestore } = getFirebase();
    const chunks: string[][] = [];
    for (let i = 0; i < extraIds.length; i += 30) chunks.push(extraIds.slice(i, i + 30));
    return chunks.map((ids) => query(collection(firestore, 'jobs'), where(documentId(), 'in', ids)));
  }, extraIds.join(','));
  const jobs = useMemo(
    () => [...openJobs, ...finishedWithReports.filter((j) => !openIds.has(j.id))],
    // openIds is derived from openJobs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openJobs, finishedWithReports],
  );
  // The very rows the "needs action" tab lists, so the number cannot differ.
  const { timesheets: sheets } = useTimesheetsByStatus('action');
  const { requests } = usePendingLeaveRequests();
  return useMemo(
    () => ({
      jobs: ordersAction(jobs, now, reportCounts),
      timesheets: timesheetsAction(sheets, now),
      leaveRequests: leaveAction(requests, now),
    }),
    [jobs, sheets, requests, now, reportCounts],
  );
}
